import { useTranslation } from 'react-i18next'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/src/renderer/components/ui/select'
import { useDisplayMode, type DisplayMode } from '@/src/renderer/contexts/DisplayModeProvider'
import { SettingsSection, SettingsRow, ShortcutInput } from '../../primitives'
import { Switch } from '@/src/renderer/components/ui/switch'
import { useQuickAddShortcut } from '@/src/renderer/hooks/useQuickAddShortcut'
import { useSoundsEnabled } from '@/src/renderer/hooks/useSoundsEnabled'
import { ReminderSection } from './ReminderSection'

/**
 * "App-Verhalten" page under "App & Bedienung".
 *
 * Electron: visibility (Dock/Menubar/Tray display mode), the global quick-note
 * shortcut, reminders and sound. Web (since v0.6.9): sound only — the other two do not
 * exist in a browser, and `useDisplayMode` has no provider there, so they live
 * in their own component that the web never mounts.
 */
export function AppBehaviorPage({ platform = 'electron' }: { platform?: 'electron' | 'web' }) {
  const { t } = useTranslation()
  const { soundsEnabled, setSoundsEnabled } = useSoundsEnabled()

  return (
    <div className="app-behavior-page">
      {platform === 'electron' && <DesktopSections />}

      <ReminderSection platform={platform} />

      {/* In Electron derselbe Wert wie der Eintrag „Sound" im Tray-Menü. */}
      <SettingsSection title={t('settings.section.sound')} variant="card">
        <SettingsRow
          label={t('preferences.appBehavior.sounds')}
          description={t(platform === 'electron'
            ? 'preferences.appBehavior.soundsHint'
            : 'preferences.appBehavior.soundsHintWeb')}
          htmlFor="app-behavior-sounds"
          control={
            <Switch
              id="app-behavior-sounds"
              checked={soundsEnabled}
              onCheckedChange={setSoundsEnabled}
            />
          }
        />
      </SettingsSection>
    </div>
  )
}

/** Only in Electron: display mode and the global quick-note shortcut. */
function DesktopSections() {
  const { t } = useTranslation()
  const { displayMode, platformModes, setDisplayMode, isLoading } = useDisplayMode()
  const { shortcut, setShortcut } = useQuickAddShortcut()
  const isMac = platformModes.modes.includes('dock-only')
  const prefix = isMac
    ? 'preferences.appBehavior.displayModes.mac'
    : 'preferences.appBehavior.displayModes.windows'
  const options = [
    { value: platformModes.modes[0] as DisplayMode, label: t(`${prefix}.${isMac ? 'dockOnly' : 'taskbarOnly'}`) },
    { value: platformModes.modes[1] as DisplayMode, label: t(`${prefix}.${isMac ? 'menubarOnly' : 'systrayOnly'}`) },
    { value: platformModes.modes[2] as DisplayMode, label: t(`${prefix}.both`) },
  ]

  return (
    <>
      <SettingsSection title={t('settings.section.visibility')} variant="card">
        <SettingsRow
          label={t('preferences.appBehavior.displayMode')}
          description={t('preferences.appBehavior.changesImmediate')}
          htmlFor="app-behavior-displaymode-select"
          control={
            isLoading ? (
              <div className="w-[280px] h-9 bg-muted animate-pulse rounded-md" aria-hidden="true" />
            ) : (
              <Select value={displayMode} onValueChange={(v) => setDisplayMode(v as DisplayMode)}>
                <SelectTrigger id="app-behavior-displaymode-select" className="w-auto gap-2">
                  <SelectValue>{options.find((o) => o.value === displayMode)?.label}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {options.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          }
        />
      </SettingsSection>

      {/*
        **„Schnell notieren" von überall** (Abnahme F-Q4, 22.09.2026). Das
        Fenster war fertig gebaut, nur führte kein Weg dorthin. Einstellbar,
        weil ein global registriertes Kürzel anderen Programmen etwas
        wegnehmen kann — wem es nicht passt, der hat so einen Ausweg.
      */}
      <SettingsSection title={t('settings.section.shortcuts')} variant="card">
        <SettingsRow
          label={t('preferences.appBehavior.quickAddShortcut')}
          description={t('preferences.appBehavior.quickAddShortcutHint')}
          htmlFor="app-behavior-quickadd-shortcut"
          control={
            <ShortcutInput
              id="app-behavior-quickadd-shortcut"
              value={shortcut}
              mac={isMac}
              onValueChange={setShortcut}
            />
          }
        />
      </SettingsSection>
    </>
  )
}
